"use client";

import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { SheetShell, useDismiss } from "@/components/odds-screen/live/top-menus";
import { BookPanel, SwitchRow } from "@/components/odds-screen/live/book-panel";
import { useMyBooksFilter } from "@/hooks/use-my-books-filter";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";

const JAKARTA = "'Plus Jakarta Sans', Inter, system-ui, sans-serif";

function Chevron({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
  );
}

export function useListedBooks(picked: PlatformType, books: readonly PlatformType[], myBooks: readonly PlatformType[] | undefined) {
  const { myBooksOnly, setMyBooksOnly } = useMyBooksFilter();
  const saved = myBooks !== undefined && myBooks.length > 0 ? myBooks : null;
  const listed = useMemo(
    () => (saved !== null && myBooksOnly ? books.filter((id) => id === picked || saved.includes(id)) : books),
    [picked, books, saved, myBooksOnly],
  );
  return { listed, saved, myBooksOnly, setMyBooksOnly };
}

export function BookPicker({
  book, picked, books, myBooks, isMobile, onPick,
}: {
  book: PlatformType | null;
  picked: PlatformType;
  books: readonly PlatformType[];
  myBooks?: readonly PlatformType[];
  isMobile: boolean;
  onPick: (id: PlatformType) => void;
}) {
  const [open, setOpen] = useState(false);
  const { listed, saved, myBooksOnly, setMyBooksOnly } = useListedBooks(picked, books, myBooks);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const pick = (id: string) => {
    if (id !== book || id === picked) onPick(id as PlatformType);
    close();
  };
  const isSelected = (id: string) => id === book;

  const label = book === null ? "Choose a book" : sportsbookIdToDisplay(book);
  const name = book === null ? label : `Book: ${label}`;
  const trigger = isMobile ? (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={name}
      onClick={() => setOpen(true)}
      className="flex h-11 min-w-0 items-center gap-1.5 rounded-lg pr-1 text-left"
      style={{ fontFamily: JAKARTA, fontWeight: 700 }}
    >
      {book !== null && <SportsbookLogoMark sportsbook={book} size={22} decorative />}
      <span className="truncate text-[17px] text-foreground">{label}</span>
      <span className="flex text-zinc-500"><Chevron size={16} /></span>
    </button>
  ) : (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={name}
      onClick={() => setOpen((value) => !value)}
      className={cn("tap flex h-8 items-center gap-1.5 rounded-md px-1.5 text-[13px] font-semibold text-foreground hover:bg-white/[0.04]", open && "bg-white/[0.06]")}
    >
      {book !== null && <SportsbookLogoMark sportsbook={book} size={18} decorative />}
      <span className="whitespace-nowrap">{label}</span>
      <span className="flex text-zinc-500"><Chevron /></span>
    </button>
  );

  const myBooksRow = saved !== null && (
    <SwitchRow checked={myBooksOnly} title="My books" detail={`${saved.length} ${saved.length === 1 ? "book" : "books"} saved in your account`} sheet={isMobile} onToggle={() => setMyBooksOnly(!myBooksOnly)} />
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <SheetShell open={open} title="Book" onClose={close}>
          {open && <BookPanel kind="single" books={listed} isSelected={isSelected} onItem={pick} sheet>{myBooksRow}</BookPanel>}
        </SheetShell>
      </>
    );
  }

  return (
    <div ref={ref} className="relative flex-shrink-0">
      {trigger}
      {open && (
        <div
          role="menu"
          aria-label="Book"
          className="absolute left-0 top-[calc(100%+6px)] z-30 flex max-h-[calc(100vh-96px)] w-[520px] max-w-[calc(100vw-32px)] origin-top-left flex-col rounded-[10px] border border-surface-line bg-surface-overlay shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)] animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none"
        >
          <BookPanel kind="single" books={listed} isSelected={isSelected} onItem={pick} sheet={false}>{myBooksRow}</BookPanel>
        </div>
      )}
    </div>
  );
}
